import React, { useState, useEffect } from 'react';
import { addEmailSubscriber } from '../services/firestoreService';
import { useAuth } from '../contexts/AuthContext';

// Blog Title Component - gradient header with user's theme color
export const BlogSectionTitle = ({ username, accentColor = '#749555' }) => {
  return (
    <div
      className="w-full max-w-2xl mx-auto mb-4 mt-12 rounded-lg p-4 py-6 text-center border-2"
      style={{
        background: `linear-gradient(to bottom, ${accentColor} 0%, #1a1a1a 100%)`,
        borderColor: accentColor
      }}
    >
      <h2 className="text-3xl font-bold text-white">Read My Blog</h2>
    </div>
  );
};

const BlogSubscribeButton = ({ profileId, username, displayLabel, source = 'blog-post', variant = 'full', accentColor = '#749555', animate = false, isOwner = false }) => {
  const { currentUser } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [wasLoggedInOnMount, setWasLoggedInOnMount] = useState(false);

  // Capture initial login state on mount
  useEffect(() => {
    setWasLoggedInOnMount(!!currentUser);
  }, []); // Only run on mount

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on mount
    checkMobile();

    // Listen for resize
    window.addEventListener('resize', checkMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Pre-fill user data if logged in
  useEffect(() => {
    if (currentUser && !submitted) {
      if (currentUser.email && !email) {
        setEmail(currentUser.email);
      }
      if (currentUser.displayName && !name) {
        setName(currentUser.displayName);
      }
    }
  }, [currentUser, submitted, email, name]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent multiple submissions
    if (submitting) {
      return;
    }

    setError('');

    // Rate limiting - 3 submissions per hour per page
    const rateLimitKey = `subscribe_attempts_${profileId}`;
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;

    try {
      const attemptsData = localStorage.getItem(rateLimitKey);
      const attempts = attemptsData ? JSON.parse(attemptsData) : [];

      // Filter out attempts older than 1 hour
      const recentAttempts = attempts.filter(timestamp => now - timestamp < oneHour);

      // Check if rate limit exceeded
      if (recentAttempts.length >= 3) {
        const oldestAttempt = Math.min(...recentAttempts);
        const timeUntilReset = Math.ceil((oneHour - (now - oldestAttempt)) / 60000); // minutes
        setError(`Rate limit exceeded. Please try again in ${timeUntilReset} minute${timeUntilReset !== 1 ? 's' : ''}.`);
        return;
      }
    } catch (e) {
      console.error('Error checking rate limit:', e);
    }

    // Validation
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setSubmitting(true);

    try {
      await addEmailSubscriber(profileId, email.trim(), name.trim(), source);

      // Record successful submission for rate limiting
      try {
        const attemptsData = localStorage.getItem(rateLimitKey);
        const attempts = attemptsData ? JSON.parse(attemptsData) : [];
        const recentAttempts = attempts.filter(timestamp => now - timestamp < oneHour);
        recentAttempts.push(now);
        localStorage.setItem(rateLimitKey, JSON.stringify(recentAttempts));
      } catch (e) {
        console.error('Error updating rate limit:', e);
      }

      setSubmitted(true);
      setName('');
      setEmail('');
    } catch (error) {
      console.error('Error subscribing:', error);
      setError('Failed to subscribe. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Compact variant - inline form for blog preview pages
  if (variant === 'compact') {
    if (submitted) {
      return (
        <div className="w-full mb-8">
          <div
            className={`flex ${isMobile || wasLoggedInOnMount ? 'flex-col items-center justify-center' : 'flex-row items-center justify-between'} gap-3 w-full p-4 rounded-lg border-2`}
            style={{
              background: isMobile
                ? `linear-gradient(to right, ${accentColor} 0%, #1a1a1a 100%)`
                : `linear-gradient(to bottom, ${accentColor} 0%, #1a1a1a 100%)`,
              borderColor: accentColor
            }}
          >
            {/* Success message - always shown */}
            <div className="flex items-center gap-2">
              <i className="fas fa-check-circle text-white text-xl"></i>
              <span className="text-lg font-semibold text-white">Subscribed!</span>
            </div>

            {/* CTA for non-logged-in users only */}
            {!wasLoggedInOnMount && (
              <a
                href={`/me?ref=${profileId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 text-white rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg hover:opacity-90 flex items-center gap-2"
                style={{
                  background: 'linear-gradient(to bottom, #9333ea 0%, #4f46e5 100%)'
                }}
              >
                <span>Start Your Own Chess Blog</span>
                <i className="fas fa-arrow-right"></i>
              </a>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="w-full mb-8 ">
        <style>
          {`
            @keyframes breathe {
              0%, 100% {
                transform: scale(1);
                box-shadow: 0 0 0 0 #9333ea40;
              }
              50% {
                transform: scale(1.02);
                box-shadow: 0 0 20px 8px #9333ea60;
              }
            }
          `}
        </style>
        <form onSubmit={handleSubmit} className="w-full">
          <div
            className={`flex ${isMobile ? 'flex-col' : 'flex-row items-center'} gap-3 w-full p-4 rounded-lg border-2 ${animate ? 'animate-breathe' : ''}`}
            style={{
              background: isMobile
                ? `linear-gradient(to right, ${accentColor} 0%, #1a1a1a 100%)`
                : `linear-gradient(to bottom, ${accentColor} 0%, #1a1a1a 100%)`,
              borderColor: accentColor,
              animation: animate ? 'breathe 1.5s ease-in-out' : undefined
            }}
          >
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              required
              className={`${isMobile ? 'w-full' : 'flex-1'} px-4 py-3 border-2 border-white/20 rounded-lg text-sm focus:outline-none focus:border-white/40 transition-colors bg-black/20 text-white placeholder-white/60`}
              disabled={submitting}
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className={`${isMobile ? 'w-full' : 'flex-1'} px-4 py-3 border-2 border-white/20 rounded-lg text-sm focus:outline-none focus:border-white/40 transition-colors bg-black/20 text-white placeholder-white/60`}
              disabled={submitting}
            />
            <button
              type="submit"
              disabled={submitting}
              className={`${isMobile ? 'w-full' : 'flex-shrink-0'} px-4 py-3 text-white rounded-lg font-semibold text-sm transition-all shadow-md flex items-center justify-center gap-2 ${
                submitting ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg hover:opacity-90'
              }`}
              style={{
                background: 'linear-gradient(to bottom, #9333ea 0%, #4f46e5 100%)'
              }}
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Subscribing...</span>
                </>
              ) : (
                <>
                  <span>Subscribe</span>
                  <i className="fas fa-envelope"></i>
                </>
              )}
            </button>
          </div>

          {/* Logged in indicator */}
          {currentUser && isOwner && (
            <div className="mt-2 text-center">
              <span className="text-red-300 text-sm">
                Logged in as @{username}
              </span>
            </div>
          )}
        </form>
        {error && (
          <div className="text-xs text-red-600 w-full mt-2">
            <i className="fas fa-exclamation-circle mr-1"></i>
            {error}
          </div>
        )}
      </div>
    );
  }

  // Full variant - card style for bottom of blog posts
  if (submitted) {
    return (
      <div className="bg-gray-800 rounded-xl border-2 border-chess-cream p-6 shadow-lg">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md border-2 border-green-600">
            <i className="fas fa-check text-green-400 text-3xl"></i>
          </div>
          <h4 className="text-2xl font-bold text-white mb-2">You're Subscribed!</h4>
          <p className="text-gray-300 mb-4">
            Thanks for subscribing! You'll receive email updates when {username || 'this player'} publishes new content.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="text-sm text-chess-cream hover:text-chess-cream/80 font-medium"
          >
            Subscribe another email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-xl border-2 border-chess-cream p-6 shadow-xl">
      <div className="text-center mb-5">
     
        <h4 className="text-2xl font-bold text-white mb-2 flex items-center justify-start gap-2">
          <span>Subscribe to {displayLabel || username || 'Updates'}</span>
        </h4>
     
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            required
            className="w-full px-4 py-3 border-2 border-gray-600 bg-gray-700 text-white rounded-lg focus:outline-none focus:border-chess-cream focus:ring-2 focus:ring-chess-cream/30 transition-all text-center placeholder-gray-400"
            disabled={submitting}
          />

          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="w-full px-4 py-3 border-2 border-gray-600 bg-gray-700 text-white rounded-lg focus:outline-none focus:border-chess-cream focus:ring-2 focus:ring-chess-cream/30 transition-all text-center placeholder-gray-400"
            disabled={submitting}
          />
        </div>

        {error && (
          <div className="bg-red-900 border border-red-600 rounded-lg p-3 flex items-center justify-center">
            <i className="fas fa-exclamation-circle text-red-400 mr-2"></i>
            <p className="text-sm text-red-200">{error}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className={`w-full px-6 py-4 border-2 border-chess-cream rounded-lg font-bold text-lg transition-all shadow-lg flex items-center justify-center ${
            submitting ? 'opacity-60 cursor-not-allowed bg-gray-700 text-gray-400' : 'bg-chess-cream text-gray-900 hover:bg-chess-cream/90 hover:shadow-xl transform hover:-translate-y-0.5'
          }`}
        >
          {submitting ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Subscribing...
            </>
          ) : (
            <>
              Subscribe to {displayLabel || username}
              <i className="fas fa-envelope ml-2"></i>

            </>
          )}
        </button>

     
      </form>
    </div>
  );
};

export default BlogSubscribeButton;
